"use client";

import React from "react";
import { cva } from "class-variance-authority";
import { ContentCard } from "@/components/ui/cards/contentCard";
import type { ContentCardItem, CardColor, ThemeVariant } from "@/types";
import { SlideUpIn, SlideUpInOut } from "../animations";
import { useIsMobile } from "@/lib/hooks";

const contentCardListVariants = cva(
  "container grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  {
    variants: {
      variant: {
        light: "",
        dark: "",
      },
    },
    defaultVariants: {
      variant: "light",
    },
  }
);

export interface ContentCardListProps {
  variant?: ThemeVariant;
  cards: ContentCardItem[];
  flowy?: boolean;
  className?: string;
}

/**
 * ContentCardList block component that displays content cards in a 3-column grid
 *
 * If cards don't specify a color, they will cycle through:
 * yellow -> purple-medium -> purple-dark -> purple-medium -> yellow...
 *
 * Usage:
 * ```tsx
 * <ContentCardList
 *   cards={[
 *     { headline: "Title", text: "Description" }, // Auto: yellow
 *     { headline: "Title", text: "Description" }, // Auto: purple-medium
 *     { headline: "Title", text: "Description", color: "yellow" }, // Explicit: yellow
 *   ]}
 * />
 * ```
 */
export const ContentCardList: React.FC<ContentCardListProps> = ({
  cards,
  variant = "light",
  flowy = false,
  className = "",
}) => {
  const isMobile = useIsMobile();

  // Color cycle: yellow -> purple-medium -> purple-dark -> purple-medium -> yellow...
  const colorCycle: CardColor[] = [
    "yellow",
    "purple-medium",
    "purple-dark",
    "purple-medium",
    "purple-dark",
  ];

  const getCardColor = (
    index: number,
    explicitColor?: CardColor
  ): CardColor => {
    if (explicitColor) {
      return explicitColor;
    }
    return colorCycle[index % colorCycle.length];
  };

  const animationDelayIncrement = flowy ? 0.1 : 0.2; // seconds

  /**
   * Helper to render a single card within its animation wrapper
   */
  const renderCard = (card: ContentCardItem, globalIndex: number) => {
    // On mobile, all cards have the same delay or minimal staggered delay
    // based on their position in the single column.
    // In "flowy" mode on desktop, the indices are based on column placement.
    const delay = isMobile
      ? (globalIndex % cards.length) * (animationDelayIncrement / 2)
      : globalIndex * animationDelayIncrement;

    const Animation = flowy ? SlideUpIn : SlideUpInOut;

    return (
      <Animation key={`card-slide-${globalIndex}`} delay={delay}>
        <ContentCard
          icon={card.icon}
          color={getCardColor(globalIndex, card.color)}
          size={card.size}
          headline={card.headline}
          label={card.label}
          text={card.text}
          link={card.link}
        />
      </Animation>
    );
  };

  return (
    <div className={contentCardListVariants({ variant, className })}>
      {flowy
        ? // Staggered (flowy) columns
          [0, 1, 2].map((colIndex) => (
            <div
              key={`col-${colIndex}`}
              className={`flex flex-col gap-md ${
                colIndex === 1 ? "lg:pt-xl" : ""
              }`}
            >
              {cards
                .filter((_, i) => i % 3 === colIndex)
                .map((card, i) => {
                  const globalIndex = i * 3 + colIndex;
                  return renderCard(card, globalIndex);
                })}
            </div>
          ))
        : // Standard grid
          cards.map((card, index) => renderCard(card, index))}
    </div>
  );
};
